import React, { useEffect, useState } from 'react';
import { Slide, Grid } from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    ChartEvent,
    ActiveElement,
    ChartOptions
} from 'chart.js';
import RequestAccessToken from "../../util/authService";
import BarChartIcon from '@material-ui/icons/BarChart';
import { refMDLoaded, practiceCoordinatorLoaded, shouldTryCareCoordinatorProfile } from '../common/ProfileHelper'

interface FacilityDataPoint {
    x: number;
    y: string;
    meta: {
        facilityId: number;
        facilityName: string;
    };
}

type OwnProps = {
    openDrillDownModal: (facilityId: number, facilityName: string) => void;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type PatientFacilitiesStatsProps = OwnProps & StateProps & DispatchProps;

const PatientFacilities = (props: PatientFacilitiesStatsProps) => {
    let {
        PatientLocationStats, requestPatientLocationStatsForProvider,
        receiveTokenResponse,
        userPracticeCoordinatorProfile,
        userRefMdProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        openDrillDownModal } = props;

    let [tokenState, setTokenState] = useState<APIStore.TokenType>();

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        ArcElement
    );

    const options: ChartOptions<'bar'> = {
        indexAxis: 'y' as const,
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Top 10 Admitting Facilities'
            }
        },
        scales: {
            y:{
                type: 'category'
            },
            x: {
                type: 'linear',
                beginAtZero: true
            }
        },
        onClick: (_event: ChartEvent, elements: ActiveElement[], chart: ChartJS) => {
            if (!elements.length) return;

            const { datasetIndex, index } = elements[0];
            const point = chart.data.datasets[datasetIndex].data[index] as unknown as FacilityDataPoint;

            openDrillDownModal(point.meta.facilityId, point.meta.facilityName);
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
            const target = event?.native?.target as HTMLElement;
            if (target) {
                target.style.cursor = elements.length ? "pointer" : "default";
            }
        }
    };

    let PatientFacilityCounts: APIStore.PatientFacility[] = [];

    let tokenStatePr = RequestAccessToken()
        .then((token) => {
            if (token && !tokenState) {
                setTokenState(token);
                receiveTokenResponse(token);
            }
        });

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }

    }, [APIToken, tokenState])

    useEffect(() => {
        if (tokenState?.accessToken && !PatientLocationStats.isLoaded && !PatientLocationStats.isLoading) {
            if (refMDLoaded(userRefMdProfile)) {
                requestPatientLocationStatsForProvider(tokenState.accessToken)
            }
        }
    }, [tokenState, userRefMdProfile]);

    useEffect(() => {
        if (practiceCoordinatorLoaded(userPracticeCoordinatorProfile) && tokenState?.accessToken) {
            if (userPracticeCoordinatorProfile.coordinatorProfile.defaultPractice.tagDescription) {
                requestPatientLocationStatsForProvider(tokenState.accessToken);
            } else {
                requestPatientLocationStatsForProvider(tokenState.accessToken)
            }
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);


    useEffect(() => {
        if (shouldTryCareCoordinatorProfile(userPracticeCoordinatorProfile) && tokenState?.accessToken) {
            requestCurrentUserCareCoordinatorProfile(tokenState?.accessToken);
        }
    }, [tokenState, userRefMdProfile]);



    if (PatientLocationStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>)
    }

    if (!PatientFacilityCounts || !PatientLocationStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    </Grid>
                </Grid>
            </Slide>

        )
    }

    if (PatientLocationStats) {
        PatientFacilityCounts = PatientLocationStats.patientFacilityCounts
    }

    const abrLookup = [
        ['System', 'Sys.'],
        ['Southwest', 'S.W.'],
        ['Northwest', 'N.W.'],
        ['Memorial', 'MEML.'],
        ['Fort', 'FRT.'],
        ['Terrace', 'TCE.'],
        ['Regional', 'RGNL.'],
        ['Baptist', 'BAPT.']
    ]

    const truncateFacilityName = (facilityName: string) => {
        let ret: string = facilityName;
        if (!facilityName) {

            return 'n/a';
        }
        const mnPtrn = /\([A-Z]+\)/;
        for (let abbrPair of abrLookup) {
            ret = ret.replaceAll(abbrPair[0], abbrPair[1])
        }

        let mnSearch = ret.match(mnPtrn);
        if (!mnSearch) {
            return `${ret.substring(0, 35)}`
        }

        return `${ret.substring(0, 25)} ${mnSearch[0]}`
    }

    let values: FacilityDataPoint[] = PatientFacilityCounts.map((val: APIStore.PatientFacility) => {
        return {
            x: val.patientsCount,
            y: truncateFacilityName(val.facilityName),
            meta: {
                facilityId: val.facilityId,
                facilityName: val.facilityName
            }
        }
    }).slice(0, 10);

    const data = {
        datasets: [
            {
                label: "Patients",
                data: values,
                backgroundColor: [
                    'rgba(82, 189, 228, 0.2)',
                    'rgba(48, 82, 98, 0.2)',
                    'rgba(165, 209, 229, 0.2)',
                    'rgba(84, 144, 174, 0.2)',
                    'rgba(71, 91, 100, 0.2)',
                    'rgba(141, 206, 233, 0.2)',
                    'rgba(125, 151, 158, 0.2)'
                ],
                borderColor: [
                    'rgb(82, 189, 228)',
                    '#305262',
                    'rgb(165, 209, 229)',
                    'rgb(84, 144, 174)',
                    'rgb(71, 91, 100)',
                    'rgb(141, 206, 233)',
                    '#7d979e'
                ],
                hoverOffset: 4,
                borderWidth: 2
            },
        ],
    };

    return (
        <React.Fragment>
            <div>
                <React.Fragment>
                    <Bar options={options} data={data}></Bar>
                </React.Fragment>
            </div>
        </React.Fragment>
    )
}


export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(PatientFacilities as any);